import {
  OperationStatusLabel,
  TimestampStatusLabel,
} from '@/context/services/invoice/type';
import { useGetErrorMessages } from '@/features/invoices/components/Details/Components/Context/ErrorMessagesProvider';
import {
  useOperationStatus,
  useOperationStatusChecker,
} from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useTimestampStatusChecker } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import { useTranslation } from '@/i18n';
import { SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType } from 'ap-openapi';
import { useMemo } from 'react';
import { StatusTagInfo } from '../components/ProcessingStatusTag';

export const useProcessingStatusTags = () => {
  const { t } = useTranslation();
  const operationChecker = useOperationStatusChecker();
  const operationStatus = useOperationStatus();
  const timestampChecker = useTimestampStatusChecker();
  const { getErrorMessage } = useGetErrorMessages();
  const eDocScannerErrorMessage = getErrorMessage(
    SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType.edoc_scanner_message
  );
  const statusTagsInfo = useMemo(() => {
    if (!operationChecker && !timestampChecker) return [];

    const tags: StatusTagInfo[] = [];

    // OCR Processing status
    if (operationChecker?.isOcrProcessing) {
      tags.push({
        status: 'success',
        label:
          OperationStatusLabel.ocr_processing ||
          t('invoice_ocr_processing_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    // Timestamp Creating status - only show if E_DOC_METADATA flag is enabled
    if (timestampChecker?.isCreating) {
      tags.push({
        status: 'success',
        label:
          TimestampStatusLabel.creating ||
          t('invoice_timestamp_creating_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    // If no specific statuses are active, show the operation status
    if (tags.length === 0 && operationStatus) {
      tags.push({
        status: 'default',
        label: OperationStatusLabel[operationStatus] || '',
        customIcon: undefined,
      });
    }

    if (eDocScannerErrorMessage) {
      tags.push({
        status: eDocScannerErrorMessage.type === 'warn' ? 'warning' : 'error',
        label: eDocScannerErrorMessage.label || '',
        customIcon: undefined,
        icon: true,
      });
    }

    return tags;
  }, [
    operationStatus,
    operationChecker,
    timestampChecker,
    t,
    eDocScannerErrorMessage,
  ]);

  return {
    statusTagsInfo,
  };
};
